import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, MousePointer2 } from 'lucide-react';
import { usePrefersReducedMotion } from '../lib/landingMotion';

function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
}

const DEFAULT_TRACK_ROWS = [
    { set: 1, time: '00:20', band: 'RED', progression: 'TUCK', complete: true },
    { set: 2, time: '00:20', band: 'RED', progression: 'TUCK', complete: true },
    { set: 3, time: '00:20', band: 'RED', progression: 'TUCK', complete: false },
    { set: 4, time: '00:10', band: 'BLUE', progression: 'FULL', complete: false },
    { set: 5, time: '00:10', band: 'BLUE', progression: 'FULL', complete: false },
];

const TOGGLE_SEQUENCE = [
    { rowIndex: 2, nextComplete: true },
    { rowIndex: 3, nextComplete: true },
    { rowIndex: 4, nextComplete: true },
    { rowIndex: 4, nextComplete: false },
    { rowIndex: 3, nextComplete: false },
    { rowIndex: 2, nextComplete: false },
];

const TIMING = {
    introPauseMs: 1020,
    pressMs: 220,
    pulseMs: 540,
    clickSettledMs: 180,
    stepRestMs: 200,
    returnHomeMs: 380,
    endPauseMs: 1280,
};

const CURSOR_GEOMETRY = {
    boxSizePx: 24,
    glowSizePx: 42,
    iconSizePx: 16,
    targetOffsetXRatio: 1.3,
    targetOffsetYRatio: 0.75,
};

function getTravelDuration(fromAnchor, toAnchor) {
    if (!fromAnchor || !toAnchor) {
        return 560;
    }

    const distance = Math.hypot(toAnchor.x - fromAnchor.x, toAnchor.y - fromAnchor.y);

    return Math.round(clamp(520 + distance * 2.8, 620, 1100));
}

function getCompletionSnapshot(rows) {
    return rows.map((row) => row.complete);
}

function getAnchorPosition(node, containerRect, extraHeightPx = 0) {
    if (!node || !containerRect) {
        return null;
    }

    const rect = node.getBoundingClientRect();
    const adjustedHeight = rect.height + extraHeightPx;

    return {
        x: rect.left - containerRect.left + rect.width / 2,
        y: rect.top - containerRect.top + adjustedHeight / 2,
        size: Math.max(rect.width, rect.height),
    };
}

function getCursorPosition(anchor) {
    if (!anchor) {
        return null;
    }

    const targetSize = anchor.size || CURSOR_GEOMETRY.boxSizePx;

    return {
        left: Math.round(anchor.x + targetSize * CURSOR_GEOMETRY.targetOffsetXRatio + 4),
        top: Math.round(anchor.y + targetSize * CURSOR_GEOMETRY.targetOffsetYRatio + 4),
    };
}

export default function AnimatedTrackPreview({
    title = 'Band Assisted Planche',
    rows = DEFAULT_TRACK_ROWS,
    isActive = true,
}) {
    const prefersReducedMotion = usePrefersReducedMotion();
    const previewRef = useRef(null);
    const heroStatusRef = useRef(null);
    const statusRefs = useRef([]);
    const initialCompletion = useMemo(() => getCompletionSnapshot(rows), [rows]);

    const [completedRows, setCompletedRows] = useState(initialCompletion);
    const [cursorTargetIndex, setCursorTargetIndex] = useState(null);
    const [pressedRowIndex, setPressedRowIndex] = useState(null);
    const [clickedRowIndex, setClickedRowIndex] = useState(null);
    const [heroPulseActive, setHeroPulseActive] = useState(false);
    const [isPreviewInView, setIsPreviewInView] = useState(false);
    const [cursorTravelMs, setCursorTravelMs] = useState(560);
    const [anchorPositions, setAnchorPositions] = useState({
        home: null,
        rows: [],
    });

    statusRefs.current = statusRefs.current.slice(0, rows.length);

    useEffect(() => {
        setCompletedRows(initialCompletion);
    }, [initialCompletion]);

    useEffect(() => {
        if (prefersReducedMotion) {
            setIsPreviewInView(false);
            return undefined;
        }

        const node = previewRef.current;

        if (!node || typeof IntersectionObserver === 'undefined') {
            setIsPreviewInView(true);
            return undefined;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    setIsPreviewInView(entry.isIntersecting);
                });
            },
            {
                threshold: 0.38,
                rootMargin: '0px 0px -8% 0px',
            }
        );

        observer.observe(node);

        return () => observer.disconnect();
    }, [prefersReducedMotion]);

    useEffect(() => {
        const node = previewRef.current;

        if (!node || typeof window === 'undefined') {
            return undefined;
        }

        let frameId = 0;

        const measureAnchors = () => {
            frameId = 0;

            const containerRect = node.getBoundingClientRect();

            setAnchorPositions({
                home: getAnchorPosition(heroStatusRef.current, containerRect),
                rows: rows.map((_, index) => getAnchorPosition(statusRefs.current[index], containerRect, 12)),
            });
        };

        const requestMeasure = () => {
            if (frameId) {
                return;
            }

            frameId = window.requestAnimationFrame(measureAnchors);
        };

        requestMeasure();

        let resizeObserver;

        if (typeof ResizeObserver === 'function') {
            resizeObserver = new ResizeObserver(requestMeasure);
            resizeObserver.observe(node);

            if (heroStatusRef.current) {
                resizeObserver.observe(heroStatusRef.current);
            }

            statusRefs.current.forEach((statusNode) => {
                if (statusNode) {
                    resizeObserver.observe(statusNode);
                }
            });
        }

        window.addEventListener('resize', requestMeasure);

        return () => {
            window.removeEventListener('resize', requestMeasure);

            if (resizeObserver) {
                resizeObserver.disconnect();
            }

            if (frameId) {
                window.cancelAnimationFrame(frameId);
            }
        };
    }, [rows]);

    const anchorsReady = Boolean(
        anchorPositions.home &&
            anchorPositions.rows.length === rows.length &&
            anchorPositions.rows.every((anchor) => anchor)
    );
    const shouldAutoplay = isActive && isPreviewInView && !prefersReducedMotion && anchorsReady;
    const cursorAnchor =
        (cursorTargetIndex === null ? anchorPositions.home : anchorPositions.rows[cursorTargetIndex]) ||
        anchorPositions.home;
    const cursorPosition = getCursorPosition(cursorAnchor);
    const isInteracting = pressedRowIndex !== null || clickedRowIndex !== null;

    useEffect(() => {
        const resetPreview = () => {
            setCompletedRows(initialCompletion);
            setCursorTargetIndex(null);
            setPressedRowIndex(null);
            setClickedRowIndex(null);
            setHeroPulseActive(false);
            setCursorTravelMs(560);
        };

        if (!shouldAutoplay) {
            resetPreview();
            return undefined;
        }

        let isCancelled = false;
        const timeoutIds = new Set();

        const schedule = (callback, delay) => {
            const timeoutId = window.setTimeout(() => {
                timeoutIds.delete(timeoutId);

                if (!isCancelled) {
                    callback();
                }
            }, delay);

            timeoutIds.add(timeoutId);
        };

        const runCycle = () => {
            resetPreview();

            let elapsed = TIMING.introPauseMs;
            let currentAnchor = anchorPositions.home;

            TOGGLE_SEQUENCE.forEach((step) => {
                const targetAnchor = anchorPositions.rows[step.rowIndex];

                if (!targetAnchor) {
                    return;
                }

                const travelMs = getTravelDuration(currentAnchor, targetAnchor);

                schedule(() => {
                    setCursorTravelMs(travelMs);
                    setCursorTargetIndex(step.rowIndex);
                }, elapsed);

                schedule(() => {
                    setPressedRowIndex(step.rowIndex);
                    setClickedRowIndex(step.rowIndex);
                    setHeroPulseActive(true);
                    setCompletedRows((currentRows) =>
                        currentRows.map((isComplete, index) =>
                            index === step.rowIndex ? step.nextComplete : isComplete
                        )
                    );
                }, elapsed + travelMs + TIMING.clickSettledMs);

                schedule(() => {
                    setPressedRowIndex((currentIndex) =>
                        currentIndex === step.rowIndex ? null : currentIndex
                    );
                }, elapsed + travelMs + TIMING.clickSettledMs + TIMING.pressMs);

                schedule(() => {
                    setClickedRowIndex((currentIndex) =>
                        currentIndex === step.rowIndex ? null : currentIndex
                    );
                    setHeroPulseActive(false);
                }, elapsed + travelMs + TIMING.clickSettledMs + TIMING.pulseMs);

                elapsed += travelMs + TIMING.clickSettledMs + TIMING.stepRestMs;
                currentAnchor = targetAnchor;
            });

            const returnTravelMs = getTravelDuration(currentAnchor, anchorPositions.home);

            schedule(() => {
                setCursorTravelMs(returnTravelMs);
                setCursorTargetIndex(null);
            }, elapsed);

            schedule(runCycle, elapsed + returnTravelMs + TIMING.endPauseMs);
        };

        runCycle();

        return () => {
            isCancelled = true;
            timeoutIds.forEach((timeoutId) => window.clearTimeout(timeoutId));
        };
    }, [anchorPositions.home, anchorPositions.rows, initialCompletion, shouldAutoplay]);

    return (
        <article
            ref={previewRef}
            className={`track-preview ${isInteracting ? 'is-interacting' : ''}`}
            aria-label={`${title} animated workout card preview`}
        >
            <div
                ref={heroStatusRef}
                className={`track-preview__hero-status is-complete ${heroPulseActive ? 'is-pulsing' : ''}`.trim()}
                aria-hidden="true"
            >
                <Check className="track-preview__status-check" size={12} />
            </div>

            <h3 className="track-preview__title">{title}</h3>

            <div className="track-preview__table">
                <div className="track-preview__row track-preview__row--header">
                    <span>Set</span>
                    <span>Time</span>
                    <span>Band</span>
                    <span>Prog</span>
                    <span aria-hidden="true" />
                </div>

                {rows.map((row, index) => {
                    const isComplete = completedRows[index];
                    const isTargeted = cursorTargetIndex === index;
                    const isPulsing = clickedRowIndex === index;
                    const isPressed = pressedRowIndex === index;

                    return (
                        <div
                            key={row.set}
                            className={[
                                'track-preview__row',
                                'track-preview__row--data',
                                isTargeted ? 'is-targeted' : '',
                                isPulsing ? 'is-pulsing' : '',
                            ]
                                .filter(Boolean)
                                .join(' ')}
                            style={{ '--row-delay': `${190 + index * 70}ms` }}
                        >
                            <span className="track-preview__index">{row.set}</span>
                            <span>{row.time}</span>
                            <span>{row.band}</span>
                            <span>{row.progression}</span>
                            <span className="track-preview__status-slot" aria-hidden="true">
                                <span
                                    ref={(node) => {
                                        statusRefs.current[index] = node;
                                    }}
                                    className={[
                                        'track-preview__status',
                                        isComplete ? 'is-complete' : '',
                                        isPressed ? 'is-pressed' : '',
                                        isPulsing ? 'is-pulsing' : '',
                                    ]
                                        .filter(Boolean)
                                        .join(' ')}
                                >
                                    <Check className="track-preview__status-check" size={11} />
                                </span>
                            </span>
                        </div>
                    );
                })}
            </div>

            <p className="track-preview__add">+ new set</p>
            <p className="track-preview__comment">Enter Comments Here...</p>

            {shouldAutoplay && cursorPosition ? (
                <div
                    className={`track-preview__cursor ${clickedRowIndex !== null ? 'is-clicking' : ''}`.trim()}
                    style={{
                        '--cursor-anchor-left': `${cursorPosition.left}px`,
                        '--cursor-anchor-top': `${cursorPosition.top}px`,
                        '--cursor-travel-ms': `${cursorTravelMs}ms`,
                        '--cursor-box-size': `${CURSOR_GEOMETRY.boxSizePx}px`,
                        '--cursor-glow-size': `${CURSOR_GEOMETRY.glowSizePx}px`,
                        '--cursor-icon-size': `${CURSOR_GEOMETRY.iconSizePx}px`,
                    }}
                    aria-hidden="true"
                >
                    <span className="track-preview__cursor-glow" />
                    <span className="track-preview__cursor-icon">
                        <MousePointer2 size={15} />
                    </span>
                </div>
            ) : null}
        </article>
    );
}
